import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import LazyLoad from 'react-lazyload';
import Skeleton from 'react-loading-skeleton';
import ProgressiveImage from 'react-progressive-image';

export type ImageDimensionsType = [number, number];
export type ImageProps = {
  alt?: string;
  className?: string;
  dimensions?: ImageDimensionsType;
  isInOverflowContainer?: boolean;
  src: string;
};

const useStyles = ([width, height]) =>
  makeStyles((theme: Theme) => ({
    frame: {
      height: 0,
      overflow: 'hidden',
      paddingTop: `${(height / width) * 100}%`,
      position: 'relative',

      '& span': {
        height: '100%',
        left: 0,
        position: 'absolute',
        top: 0,
        width: '100%',
      },
    },

    image: {
      display: 'block',
      maxWidth: '100%',
    },

    loadingAnimation: {
      borderRadius: `${theme.shape.borderRadius}`,
    },
  }))();

function ImageLoading(props): JSX.Element {
  const { alt = '', dimensions = [112, 112] } = props;
  const classes = useStyles(dimensions);

  return (
    <div
      aria-label={alt}
      className={classes.frame}
      data-fd="image-loader"
      role="img"
    >
      <Skeleton
        className={classes.loadingAnimation}
        height="100%"
        width="100%"
      />
    </div>
  );
}

export function Image(props: ImageProps): JSX.Element {
  const {
    alt = '',
    className = '',
    dimensions = [112, 112],
    isInOverflowContainer = false,
    src,
  } = props;
  const classes = useStyles([...dimensions]);

  const [width, height] = dimensions;
  const srcWithConfig = `${src}?auto=format&auto=compress&fit=crop&w=${width}&h=${height}`;

  const imageWrapperStyles = clsx(className && className);
  const imageStyles = clsx(classes.image, 'fd-image');

  return (
    <div className={imageWrapperStyles}>
      <LazyLoad
        height={height}
        once
        overflow={isInOverflowContainer}
        placeholder={<ImageLoading props={classes} />}
        width={width}
      >
        <ProgressiveImage src={srcWithConfig} placeholder="">
          {(source, loading): JSX.Element =>
            loading ? (
              <ImageLoading {...props} />
            ) : (
              <img
                alt={alt}
                aria-label={alt}
                className={imageStyles}
                height={height}
                src={source}
                srcSet={`${source}&dpr=1 1x, ${source}&dpr=2 2x, ${source}&dpr=3 3x`}
                width={width}
              />
            )
          }
        </ProgressiveImage>
      </LazyLoad>
    </div>
  );
}
